import { create } from 'zustand';
import { useCallback, useMemo } from 'react';
import { useChatStore } from './chatStore';
import type { ISegment } from '@typing/wsui/getMessages';

/**
 * 引用文献抽屉 UI 状态管理
 * 只存储标识符，不存储数据
 */
interface IReferenceStoreState {
    // 抽屉是否可见
    visible: boolean;
    // 当前 sessionId
    sessionId: string | null;
    // 当前消息 ID
    messageId: string | null;
    // 锚点（引用编号，如 "1", "2"）
    anchorIndex: string | null;
    // 段落索引（同一锚点出现的第几次，从 0 开始）
    paragraphIndex: number | null;
    // 触发器：确保相同参数也能触发更新
    triggerTimestamp: number | null;
    // 打开抽屉
    openDrawer: (sessionId: string, messageId: string, anchorIndex?: string, paragraphIndex?: number) => void;
    // 关闭抽屉
    closeDrawer: () => void;
}

/**
 * 引用文献抽屉 Store（内部使用）
 */
const useReferenceStoreInternal = create<IReferenceStoreState>((set) => ({
    visible: false,
    sessionId: null,
    messageId: null,
    anchorIndex: null,
    paragraphIndex: null,
    triggerTimestamp: null,

    openDrawer: (sessionId, messageId, anchorIndex, paragraphIndex) =>
        set({
            visible: true,
            sessionId,
            messageId,
            anchorIndex: anchorIndex ?? null,
            paragraphIndex: paragraphIndex ?? null,
            // 每次调用都更新时间戳，确保即使参数相同也能触发更新
            triggerTimestamp: Date.now(),
        }),
    closeDrawer: () =>
        set({
            visible: false,
            sessionId: null,
            messageId: null,
            anchorIndex: null,
            paragraphIndex: null,
            triggerTimestamp: null,
        }),
}));

/**
 * 引用文献抽屉对外接口
 * 组合 referenceStore 和 chatStore，返回实时数据
 */
export interface IReferenceStoreResult {
    // 抽屉是否可见
    visible: boolean;
    // 当前 segment（实时数据，从 chatStore 读取）
    currentSegment: ISegment | null;
    // 当前 sessionId
    sessionId: string;
    // 当前 segment 所属的消息 ID
    messageId: string;
    // 锚点（引用编号）
    anchorIndex: string | null;
    // 段落索引
    paragraphIndex: number | null;
    // 触发器时间戳（用于检测相同参数的重复打开）
    triggerTimestamp: number | null;
    // 打开抽屉
    openDrawer: (sessionId: string, messageId: string, anchorIndex?: string, paragraphIndex?: number) => void;
    // 关闭抽屉
    closeDrawer: () => void;
}

/**
 * 引用文献抽屉 Hook
 * 使用者不需要关心数据来源，直接使用 currentSegment 即可
 */
export const useReferenceStore = (): IReferenceStoreResult => {
    const { visible, sessionId, messageId, anchorIndex, paragraphIndex, triggerTimestamp, openDrawer, closeDrawer } =
        useReferenceStoreInternal();

    // 抽屉打开时订阅 sessions 实时数据；关闭时返回 null，不响应 chatStore 变化
    const sessions = useChatStore(useCallback((state) => (visible ? state.sessions : null), [visible]));

    const currentSegment = useMemo<ISegment | null>(() => {
        if (!sessions || !sessionId || !messageId) return null;
        const session = sessions[sessionId];
        if (!session) return null;
        const msg = session.messages.find((m) => m.message_id === messageId);
        if (!msg) return null;
        // 优先在所有 sections 中查找 oeReferences（包含完整 feature/tool_tip）
        for (const section of msg.sections) {
            const oeSeg = section.segments.find((s) => s.type === 'oeReferences');
            if (oeSeg) return oeSeg;
        }
        // fallback 到 thinkProcessV2
        for (const section of msg.sections) {
            const thinkSeg = section.segments.find((s) => s.type === 'thinkProcessV2');
            if (thinkSeg) return thinkSeg;
        }
        return null;
    }, [sessions, sessionId, messageId]);

    return {
        visible,
        currentSegment,
        sessionId: sessionId || '',
        messageId: messageId || '',
        anchorIndex,
        paragraphIndex,
        triggerTimestamp,
        openDrawer,
        closeDrawer,
    };
};
